/**
 * React hooks for Google Analytics integration
 * Provides easy-to-use hooks for tracking pages, modals, and events
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '../services/analyticsService';

/**
 * Hook to automatically track page views on route changes
 * Should be used once at the app level (e.g., in App.tsx or Layout)
 */
export function usePageTracking(): void {
  const location = useLocation();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Only track if path actually changed
    if (previousPath.current !== location.pathname) {
      analytics.trackPageView(location.pathname + location.search);
      previousPath.current = location.pathname;
    }
  }, [location.pathname, location.search]);
}

/**
 * Hook to track modal open/close events
 * @param modalName - Unique name for the modal
 * @param isOpen - Whether the modal is currently open
 * @param additionalParams - Additional params to track
 */
export function useModalTracking(
  modalName: string,
  isOpen: boolean,
  additionalParams?: Record<string, string | number>
): void {
  const wasOpen = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      // Modal just opened
      analytics.trackModalOpen(modalName, additionalParams);
    } else if (!isOpen && wasOpen.current) {
      // Modal just closed
      analytics.trackModalClose(modalName, additionalParams);
    }
    wasOpen.current = isOpen;
  }, [isOpen, modalName, additionalParams]);
}

/**
 * Hook to get tracking functions for events
 * Returns memoized tracking functions
 */
export function useAnalytics() {
  const trackEvent = useCallback(
    (eventName: string, params?: Record<string, string | number | boolean | undefined>) => {
      analytics.trackEvent(eventName, params);
    },
    []
  );

  const trackButtonClick = useCallback(
    (buttonName: string, params?: Record<string, string | number | boolean | undefined>) => {
      analytics.trackButtonClick(buttonName, params);
    },
    []
  );

  const trackFormSubmit = useCallback(
    (formName: string, success: boolean, params?: Record<string, string | number | boolean | undefined>) => {
      analytics.trackFormSubmit(formName, success, params);
    },
    []
  );

  const trackFeatureUsage = useCallback(
    (featureName: string, action: string, params?: Record<string, string | number | boolean | undefined>) => {
      analytics.trackFeatureUsage(featureName, action, params);
    },
    []
  );

  const trackAIAutomation = useCallback(
    (
      action: 'enabled' | 'disabled' | 'search_started' | 'nego_started' | 'result_found' | 'booking_confirmed',
      params?: Record<string, string | number | boolean | undefined>
    ) => {
      analytics.trackAIAutomation(action, params);
    },
    []
  );

  const trackError = useCallback(
    (errorType: string, errorMessage: string, params?: Record<string, string | number | boolean | undefined>) => {
      analytics.trackError(errorType, errorMessage, params);
    },
    []
  );

  const trackTiming = useCallback(
    (category: string, variable: string, valueMs: number) => {
      analytics.trackTiming(category, variable, valueMs);
    },
    []
  );

  return {
    trackEvent,
    trackButtonClick,
    trackFormSubmit,
    trackFeatureUsage,
    trackAIAutomation,
    trackError,
    trackTiming,
  };
}

/**
 * Hook to track component mount timing
 * Useful for measuring render performance
 */
export function useComponentTiming(componentName: string): void {
  const mountTime = useRef(performance.now());

  useEffect(() => {
    const loadTime = performance.now() - mountTime.current;
    analytics.trackTiming('component_load', componentName, loadTime);
  }, [componentName]);
}

/**
 * Hook to track user engagement time on a page/component
 */
export function useEngagementTracking(pageName: string): void {
  const startTime = useRef<number | null>(null);
  const isVisible = useRef(true);
  const totalTime = useRef(0);

  useEffect(() => {
    startTime.current = performance.now();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page became hidden, pause timer
        if (startTime.current !== null) {
          totalTime.current += performance.now() - startTime.current;
          startTime.current = null;
        }
        isVisible.current = false;
      } else {
        // Page became visible, resume timer
        startTime.current = performance.now();
        isVisible.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Track total engagement time on unmount
      if (startTime.current !== null) {
        totalTime.current += performance.now() - startTime.current;
      }

      if (totalTime.current > 1000) {
        // Only track if > 1 second
        analytics.trackEvent('engagement', {
          page_name: pageName,
          engagement_time_ms: Math.round(totalTime.current),
        });
      }
    };
  }, [pageName]);
}
